import React from 'react';
import PropTypes from 'prop-types';
import { useTranslations, useLocale } from 'next-intl';
import { connect } from 'react-redux';
import Link from 'next/link';

import { LongArrowRight } from '@components/icons/arrow/arrow';
import Button from '@components/atoms/button/button';
import Accordion from '@components/organisms/accordion/accordion';
import Container from '@components/layout/container/container';
import ContentBlocks from '@components/organisms/content-blocks/contentBlocks.withRedux';
import Stage from '@components/organisms/stage/stage';
import FilterResults from '@components/organisms/booking-filter/components/filter-results/filter-results';
import Headline from '@components/atoms/headline/headline';
import TemplateDefault from '@components/templates/default/default';
import track from '@helper/tracking';
import StyledDateDetails from './tourDetails.style';

const TourDetails = ({
  embeds,
  widgetUrl,
  registerable = false,
  bookable = false,
  shopUrl,
  furtherTeasers
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const ticketShopLabel = t('calendar.requestBooking');

  return (
    <TemplateDefault>
      <StyledDateDetails>
        <Stage
          shopText={ticketShopLabel}
          shopUrl={shopUrl && (registerable || bookable) ? shopUrl : null}
        />

        {embeds && <ContentBlocks noShareLinks />}

        {widgetUrl && (registerable || bookable) && (
          <Container>
            <Accordion
              onChange={items => {
                if (items.length > 0) {
                  track(['trackEvent', 'Booking', 'Click', t('accordion.bookTickets')]);
                }
              }}
              accordion={{
                sections: [
                  {
                    subline: t('accordion.bookTickets'),
                    type: 'iframe',
                    data: {
                      title: 'Ticket widget',
                      width: 800,
                      height: 600,
                      file: widgetUrl
                    }
                  }
                ]
              }}
            />

            {shopUrl && (registerable || bookable) && (
              <Link
                href={shopUrl}
                title={ticketShopLabel}
                className="gomusShopLink"
                rel="noopener noreferrer"
                target={shopUrl.includes('tickets.futurium.') ? undefined : '_blank'}
                data-gomus-embed={shopUrl.includes('tickets.futurium.') ? true : undefined}
              >
                <Button onClick={() => track(['trackEvent', 'Booking', 'Click', ticketShopLabel])}>
                  {ticketShopLabel}
                </Button>
              </Link>
            )}
          </Container>
        )}

        {(furtherTeasers?.showAllLink || furtherTeasers?.teasers) && (
          <Container
            className={`dateDetails__further-teasers dateDetails__further-teaser--${
              furtherTeasers?.teasers && Object.keys(furtherTeasers.teasers).length > 0
                ? 'with-results'
                : 'no-results'
            }`}
          >
            <Headline tag="h3" secondary>
              {locale === 'de' ? 'Mehr Events' : 'More Events'}
            </Headline>

            {furtherTeasers?.teasers &&
              Object.entries(furtherTeasers.teasers).map(([date, results], index) => (
                <FilterResults
                  key={date + index}
                  settings={{ date: new Date(date) }}
                  loading={false}
                  error={false}
                  results={{ results }}
                />
              ))}

            {furtherTeasers?.showAllLink && (
              <Link href={furtherTeasers?.showAllLink}>
                <Headline className="dateDetails__more-btn" tag="p" subline>
                  {locale === 'de' ? 'Alle Events' : 'All Events'}
                  <LongArrowRight />
                </Headline>
              </Link>
            )}
          </Container>
        )}
      </StyledDateDetails>
    </TemplateDefault>
  );
};

TourDetails.propTypes = {
  embeds: PropTypes.arrayOf(PropTypes.shape()),
  widgetUrl: PropTypes.string,
  registerable: PropTypes.bool,
  bookable: PropTypes.bool,
  shopUrl: PropTypes.string,
  furtherTeasers: PropTypes.shape()
};

const mapStateToProps = state => ({
  widgetUrl: state.app.pageData?.widgetUrl,
  registerable: state.app.pageData?.registerable,
  shopUrl: state.app.pageData?.shopUrl,
  bookable: state.app.pageData?.bookable,
  furtherTeasers: state.app.pageData?.furtherTeasers,
  embeds: state.app.pageData?.embeds
});

export default connect(mapStateToProps)(TourDetails);
